import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';

import { AccountModel } from '../../../../../../domain/models/account.model';
import { CasheringService } from '../../../../../../domain/services/cashering/cashering.service';
import { DialogService } from '../../../../../../domain/services/dialog/dialog.service';
import { FeeOverride, PlanDetail, PlanPreview, ReturnSummaryDto } from '../../../../../../domain/models/return.model';
import { ReturnDetailComponent } from '../../../../../cashering/return-detail/return-detail.component';

type ReturnsView = 'list' | 'type' | 'regular' | 'assessment' | 'installment' | 'lien' | 'detail' | 'plan';

interface CreateTypeCard {
  key: 'REGULAR' | 'INSTALLMENT' | 'ASSESMENT' | 'LIEN';
  title: string;
  description: string;
  enabled: boolean; // phased: only REGULAR is wired in Phase 1
}

const MONTHS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

@Component({
  selector: 'app-account-returns-tab',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule,
    MatTableModule, MatPaginatorModule, MatSortModule,
    MatFormFieldModule, MatSelectModule, MatInputModule,
    MatButtonModule, MatIconModule, MatTooltipModule, MatCardModule, MatSlideToggleModule, MatCheckboxModule,
    MatDatepickerModule, MatNativeDateModule,
    ReturnDetailComponent,
  ],
  templateUrl: './account-returns-tab.component.html',
  styleUrl: './account-returns-tab.component.css',
})
export class AccountReturnsTabComponent implements OnInit, AfterViewInit {
  @Input() account!: AccountModel;
  // Setters, not plain fields: the table lives inside *ngIf="view === 'list'", so coming back from
  // the detail/plan view creates a NEW paginator/sort. Wiring them only in ngAfterViewInit left the
  // new paginator unattached ("0 of 0" until reload — QA pbstest 2026-10-05).
  paginator!: MatPaginator;
  sort!: MatSort;
  @ViewChild(MatPaginator) set paginatorRef(p: MatPaginator) {
    this.paginator = p;
    if (p) { this.dataSource.paginator = p; }
  }
  @ViewChild(MatSort) set sortRef(s: MatSort) {
    this.sort = s;
    if (s) { this.dataSource.sort = s; }
  }

  view: ReturnsView = 'list';
  loading = false;

  /** The return shown full-page in the 'detail' view (Search path, not a modal). */
  selectedReturnId?: number;
  detailEdit = false;
  /** Rolled-up returns open read-only (they belong to a plan/assessment/lien). */
  detailReadOnly = false;
  detailPlanLabel = '';

  displayedColumns = ['period', 'type', 'dueDate', 'properties', 'status', 'totalAmountDue'];
  dataSource = new MatTableDataSource<ReturnSummaryDto>([]);
  allReturns: ReturnSummaryDto[] = [];

  // Filters
  years: number[] = [];
  selectedYear: number | 'all' = 'all';
  selectedType: string = 'all';
  selectedStatus: string = 'all';

  readonly statuses = ['UNPAID', 'PAID', 'PARTIALLY_PAID', 'OVERDUE', 'CANCELLED'];
  readonly types = ['REGULAR', 'DUPLICATE', 'AMENDED', 'INSTALLMENT', 'ASSESSMENT', 'LIEN'];

  // Counters
  unpaidCount = 0;
  partiallyPaidCount = 0;
  dueCount = 0;

  // Create flow
  readonly months = MONTHS;
  readonly frequencies = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUAL', label: 'Annual' },
    { value: 'SEASONAL', label: 'Seasonal' },
  ];
  createTypeCards: CreateTypeCard[] = [
    { key: 'REGULAR', title: 'REGULAR', description: 'Create a brand new return or a duplicate one', enabled: true },
    { key: 'INSTALLMENT', title: 'INSTALLMENT', description: 'For financial-hardship cases where a structured plan ensures gradual settlement.', enabled: true },
    { key: 'ASSESMENT', title: 'ASSESMENT', description: 'Roll delinquent returns into one lump-sum assessment, or estimate unreported periods.', enabled: true },
    { key: 'LIEN', title: 'LIEN', description: 'Group delinquent returns into a lien after due-process notices have been provided.', enabled: true },
  ];
  selectedCreateType: CreateTypeCard['key'] = 'REGULAR';
  regularForm!: FormGroup;
  saving = false;

  // Assessment: 'rollup' (lump-sum over existing returns) vs 'swag' (estimated periods)
  assessmentMode: 'rollup' | 'swag' = 'rollup';
  assessmentForm!: FormGroup;
  rollupForm!: FormGroup;
  readonly causes = [
    { value: 'UNDERREPORTED', label: 'Underreported receipts' },
    { value: 'INCORRECT_DEDUCTIONS', label: 'Incorrect deductions/exemptions' },
    { value: 'OFFICE_ADJUSTMENT', label: 'Tax-office adjustment' },
    { value: 'AUDIT_FINDING', label: 'Audit finding' },
  ];
  /** Roll-up adds no value change, so it gets its own cause first (Todd, QA 2026-10-06). */
  readonly rollupCauses = [
    { value: 'DELINQUENT_ROLLUP', label: 'Delinquent — unpaid returns rolled up' },
    ...this.causes,
  ];

  // Installment
  installmentForm!: FormGroup;
  selectedReturnIds = new Set<number>();
  private previewSeq = 0;

  // Preview (shared by installment / rollup assessment / lien wizards)
  preview?: PlanPreview;
  /** Editable penalty/interest cells keyed by returnId. */
  overrideEdits: Record<number, { penalty: number | null; interest: number | null }> = {};

  // Lien
  lienForm!: FormGroup;

  // Plan detail ('plan' view) for INSTALLMENT / ASSESSMENT / LIEN rows
  planReturn?: ReturnSummaryDto;
  planDetail?: PlanDetail;
  planUnderlying: ReturnSummaryDto[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly casheringService: CasheringService,
    private readonly dialogService: DialogService,
    private readonly router: Router,
    private readonly dialog: MatDialog
  ) {}

  /**
   * Show the return editor full-page inside this tab (Search path — NOT a modal;
   * the modal is reserved for the cashiering flow). Swaps the RETURNS tab content
   * from the list to the embedded editor.
   */
  private openReturnDetail(returnId: number, edit = false, readOnly = false, planLabel = ''): void {
    if (!this.account?.id) { return; }
    this.selectedReturnId = returnId;
    this.detailEdit = edit && !readOnly;
    this.detailReadOnly = readOnly;
    this.detailPlanLabel = planLabel;
    this.view = 'detail';
  }

  /** Editor asked to close; return to the list and refresh if a save happened. */
  onDetailBack(savedDirty: boolean): void {
    this.selectedReturnId = undefined;
    this.view = 'list';
    if (savedDirty) { this.loadReturns(); }
  }

  ngOnInit(): void {
    this.regularForm = this.fb.group({
      month: ['', Validators.required],
      year: [new Date().getFullYear(), Validators.required],
      frequency: ['MONTHLY', Validators.required],
      enableCustomerEditing: [true],
    });
    this.assessmentForm = this.fb.group({
      cause: ['UNDERREPORTED', Validators.required],
      auditReference: [''],
      statuteBasis: [''],
      overrideSol: [false],
      justification: [''],
      periods: this.fb.array([this.newPeriodGroup()]),
    });
    this.rollupForm = this.fb.group({
      cause: ['DELINQUENT_ROLLUP', Validators.required],
      auditReference: [''],
      statuteBasis: [''],
      dueDate: [null, Validators.required],
      calculationDate: [null],
    });
    this.installmentForm = this.fb.group({
      downPaymentAmount: [0, [Validators.min(0)]],
      installmentCount: [6, [Validators.required, Validators.min(1)]],
      firstDueDate: [null, Validators.required],
    });
    this.lienForm = this.fb.group({
      noticeSentDate: [null, Validators.required],
      demandIssuedDate: [null, Validators.required],
      cureDeadline: [null, Validators.required],
      statuteBasis: [''],
      lienRecordedReference: [''],
      lienFeeAmount: [null],
      dueDate: [null],
    });
    this.buildYears();
    this.loadReturns();
  }

  // ===== Plan detail view (INSTALLMENT / ASSESSMENT / LIEN rows) =====

  isPlanType(r: ReturnSummaryDto): boolean {
    return r.type === 'INSTALLMENT' || (r.type as string) === 'ASSESSMENT' || (r.type as string) === 'LIEN';
  }

  private openPlanView(r: ReturnSummaryDto): void {
    this.planReturn = r;
    this.planDetail = undefined;
    // Underlying rolled-up returns share the same case/plan id.
    this.planUnderlying = this.allReturns.filter(u => u.rolledUp && (
      (r.installmentPlanId && u.installmentPlanId === r.installmentPlanId) ||
      ((r as any).assessmentCaseId && u.assessmentCaseId === r.assessmentCaseId && (r.type as string) === 'ASSESSMENT') ||
      ((r as any).lienCaseId && u.lienCaseId === r.lienCaseId && (r.type as string) === 'LIEN')
    ));
    this.view = 'plan';
    if (r.type === 'INSTALLMENT' && r.installmentPlanId) {
      this.casheringService.getPlanDetail(r.installmentPlanId).subscribe({
        next: (detail) => { this.planDetail = detail; },
        error: () => { /* the underlying list still renders */ },
      });
    }
  }

  openPlanUnderlying(u: ReturnSummaryDto): void {
    this.openReturnDetail(u.id, false, true, this.planLabelOf(u));
  }

  openPlanReturnDetail(): void {
    if (this.planReturn) { this.openReturnDetail(this.planReturn.id, false, true); }
  }

  cancelPlan(): void {
    const r = this.planReturn;
    if (!r) { return; }
    const doCancel = () => {
      const obs = r.type === 'INSTALLMENT' && r.installmentPlanId
        ? this.casheringService.cancelStaffPlan(r.installmentPlanId)
        : (r.type as string) === 'ASSESSMENT' && r.assessmentCaseId
          ? this.casheringService.cancelAssessment(r.assessmentCaseId)
          : r.lienCaseId
            ? this.casheringService.cancelLien(r.lienCaseId)
            : null;
      if (!obs) { return; }
      this.saving = true;
      obs.subscribe({
        next: () => {
          this.saving = false;
          this.dialogService.openSuccesDialog('Cancelled',
            'The plan was cancelled and its returns are payable again.');
          this.backToList();
        },
        error: (err) => {
          this.saving = false;
          this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not cancel.');
        },
      });
    };
    doCancel();
  }

  private planLabelOf(u: ReturnSummaryDto): string {
    if (u.lienCaseId) { return `Part of Lien #${u.lienCaseId}`; }
    if (u.assessmentCaseId) { return `Part of Assessment #${u.assessmentCaseId}`; }
    if (u.installmentPlanId) { return `Part of Installment Plan #${u.installmentPlanId}`; }
    return '';
  }

  // ===== Lien =====

  /** Delinquent, not already liened/rolled-up, owned by this account (not shared) — eligible for a lien. */
  get delinquentReturns(): ReturnSummaryDto[] {
    return this.allReturns.filter(r =>
      !r.shared && !r.lienCaseId && !r.rolledUp && !this.isPlanType(r) &&
      (r.status === 'OVERDUE' || r.status === 'UNPAID' || r.status === 'PARTIALLY_PAID'
        // Past due but not yet flipped to OVERDUE by the nightly pass (QA 2026-10-06).
        || (r.status === 'PENDING' && !!r.dueDate && r.dueDate.substring(0, 10) < this.todayIso())));
  }

  private todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private toIsoDate(d: any): string {
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private lienRequest(): any {
    const raw = this.lienForm.value;
    return {
      accountId: this.account.id,
      returnIds: Array.from(this.selectedReturnIds),
      noticeSentDate: this.toIsoDate(raw.noticeSentDate),
      demandIssuedDate: this.toIsoDate(raw.demandIssuedDate),
      cureDeadline: this.toIsoDate(raw.cureDeadline),
      statuteBasis: raw.statuteBasis || undefined,
      lienRecordedReference: raw.lienRecordedReference || undefined,
      lienFeeAmount: raw.lienFeeAmount != null && raw.lienFeeAmount !== '' ? Number(raw.lienFeeAmount) : undefined,
      dueDate: raw.dueDate ? this.toIsoDate(raw.dueDate) : undefined,
    };
  }

  onPreviewLien(): void {
    if (!this.validLienInput()) { return; }
    this.saving = true;
    this.casheringService.previewLien(this.lienRequest()).subscribe({
      next: (p) => { this.saving = false; this.preview = p; },
      error: (err) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not preview the lien.');
      },
    });
  }

  onCreateLien(): void {
    if (!this.validLienInput()) { return; }
    this.confirmZeroFiling('lien', () => this.createLien());
  }

  private createLien(): void {
    this.saving = true;
    this.casheringService.createLien(this.lienRequest()).subscribe({
      next: () => {
        this.saving = false;
        this.dialogService.openSuccesDialog('Lien recorded',
          'The lien was recorded and its lump-sum return created.');
        this.backToList();
      },
      error: (err) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not record the lien.');
      },
    });
  }

  private validLienInput(): boolean {
    if (!this.account?.id) { return false; }
    if (this.selectedReturnIds.size === 0) {
      this.dialogService.openErrorDialog('No returns selected', 'Select at least one delinquent return.');
      return false;
    }
    if (this.lienForm.invalid) { this.lienForm.markAllAsTouched(); return false; }
    if (!(new Date(this.lienForm.value.cureDeadline) < new Date())) {
      this.dialogService.openErrorDialog('Cure window not elapsed',
        'The cure deadline must be in the past before a lien can be recorded.');
      return false;
    }
    return true;
  }

  // ===== Installment =====

  /**
   * Returns eligible for an installment plan / assessment roll-up: outstanding, not rolled up, not a plan
   * return, and owned by this account — a shared return (this account is a guest) is owner-only (spec v1).
   */
  get eligibleReturns(): ReturnSummaryDto[] {
    return this.allReturns.filter(r =>
      !r.shared && !r.installmentPlanId && !r.rolledUp && !this.isPlanType(r) &&
      (r.status === 'UNPAID' || r.status === 'OVERDUE' || r.status === 'PARTIALLY_PAID' || r.status === 'PENDING'));
  }

  isSelected(r: ReturnSummaryDto): boolean { return this.selectedReturnIds.has(r.id); }

  /** $0 due on an open return = never filed (a filed no-rent month is ZERO_RETURN and never lists here). */
  isUnfiled(r: ReturnSummaryDto): boolean { return (r.balanceDue ?? r.totalAmountDue ?? 0) <= 0; }

  /**
   * Selected $0 returns are submitted as zero returns by the server and left out of the
   * plan / roll-up / lien. Confirm that first; run {@code go} straight away when there are none.
   */
  private confirmZeroFiling(what: string, go: () => void): void {
    const zeros = this.allReturns.filter(r => this.selectedReturnIds.has(r.id) && this.isUnfiled(r));
    if (!zeros.length) { go(); return; }
    const periods = zeros.map(r => r.period).join(', ');
    this.dialogService.openConfirmationDialog(
      'Submit $0 returns as zero returns?',
      `${zeros.length} selected return(s) have nothing filed (${periods}). They will be submitted as `
        + `$0 zero returns (no rent), closed, and left out. The ${what} will include only the returns with an amount due.`,
      (confirmed: boolean) => { if (confirmed) { go(); } });
  }

  toggleSelect(r: ReturnSummaryDto, checked: boolean): void {
    if (checked) { this.selectedReturnIds.add(r.id); } else { this.selectedReturnIds.delete(r.id); }
    this.preview = undefined; // selection changed; the preview is stale
    this.previewSeq++;        // and any preview still in flight is too
  }

  get allSelected(): boolean {
    return this.eligibleReturns.length > 0 && this.eligibleReturns.every(r => this.selectedReturnIds.has(r.id));
  }

  toggleAll(checked: boolean): void {
    this.eligibleReturns.forEach(r => this.toggleSelect(r, checked));
  }

  get selectedTotal(): number {
    return this.eligibleReturns
      .filter(r => this.selectedReturnIds.has(r.id))
      .reduce((sum, r) => sum + (r.balanceDue ?? r.totalAmountDue ?? 0), 0);
  }

  /** Overrides currently entered in the preview table (only edited cells are sent). */
  private collectOverrides(): FeeOverride[] | undefined {
    const list: FeeOverride[] = [];
    for (const [id, edit] of Object.entries(this.overrideEdits)) {
      if (edit.penalty != null || edit.interest != null) {
        list.push({ returnId: Number(id), penalty: edit.penalty, interest: edit.interest });
      }
    }
    return list.length ? list : undefined;
  }

  onOverrideChanged(): void {
    // Amounts changed; totals must be recomputed server-side before creating.
    if (this.view === 'installment') { this.onPreviewInstallment(); }
    else if (this.view === 'assessment') { this.onPreviewRollup(); }
  }

  private installmentRequest(): any {
    const raw = this.installmentForm.value;
    return {
      accountId: this.account.id,
      returnIds: Array.from(this.selectedReturnIds),
      downPaymentAmount: Number(raw.downPaymentAmount) || 0,
      installmentCount: Number(raw.installmentCount),
      firstDueDate: raw.firstDueDate ? this.toIsoDate(raw.firstDueDate) : undefined,
      overrides: this.collectOverrides(),
    };
  }

  onPreviewInstallment(): void {
    if (!this.account?.id) { return; }
    if (this.selectedReturnIds.size === 0) {
      this.dialogService.openErrorDialog('No returns selected', 'Select at least one return for the plan.');
      return;
    }
    if (this.installmentForm.invalid) { this.installmentForm.markAllAsTouched(); return; }
    this.saving = true;
    const seq = ++this.previewSeq;
    this.casheringService.previewStaffPlan(this.installmentRequest()).subscribe({
      next: (p) => {
        this.saving = false;
        if (seq !== this.previewSeq) { return; } // inputs changed since: a newer preview (or none) applies
        this.preview = p;
        this.seedOverrideEdits(p);
      },
      error: (err) => {
        this.saving = false;
        if (seq !== this.previewSeq) { return; }
        this.preview = undefined; // don't leave a schedule on screen for inputs the server refused
        this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not preview the plan.');
      },
    });
  }

  private seedOverrideEdits(p: PlanPreview): void {
    for (const row of p.returns) {
      if (!this.overrideEdits[row.returnId]) {
        this.overrideEdits[row.returnId] = { penalty: null, interest: null };
      }
    }
  }

  onCreateInstallment(): void {
    if (!this.preview) { this.onPreviewInstallment(); return; }
    if (this.preview.pendingRateReturnIds.length) {
      this.dialogService.openErrorDialog('Interest rate missing',
        'An interest rate is not posted yet for one or more periods; the plan total cannot be computed.');
      return;
    }
    this.confirmZeroFiling('installment plan', () => this.createInstallment());
  }

  private createInstallment(): void {
    this.saving = true;
    this.casheringService.createStaffPlan(this.installmentRequest()).subscribe({
      next: () => {
        this.saving = false;
        this.dialogService.openSuccesDialog('Plan created',
          'The installment plan and its installment returns were created.');
        this.backToList();
      },
      error: (err) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not create the plan.');
      },
    });
  }

  // ===== Assessment (roll-up) =====

  setAssessmentMode(mode: 'rollup' | 'swag'): void {
    this.assessmentMode = mode;
    this.preview = undefined;
  }

  private rollupRequest(): any {
    const raw = this.rollupForm.value;
    return {
      accountId: this.account.id,
      returnIds: Array.from(this.selectedReturnIds),
      dueDate: this.toIsoDate(raw.dueDate),
      cause: raw.cause,
      auditReference: raw.auditReference || undefined,
      statuteBasis: raw.statuteBasis || undefined,
      calculationDate: raw.calculationDate ? this.toIsoDate(raw.calculationDate) : undefined,
      overrides: this.collectOverrides(),
    };
  }

  onPreviewRollup(): void {
    if (!this.account?.id) { return; }
    if (this.selectedReturnIds.size === 0) {
      this.dialogService.openErrorDialog('No returns selected', 'Select at least one return to assess.');
      return;
    }
    if (this.rollupForm.invalid) { this.rollupForm.markAllAsTouched(); return; }
    this.saving = true;
    this.casheringService.previewRollupAssessment(this.rollupRequest()).subscribe({
      next: (p) => {
        this.saving = false;
        this.preview = p;
        this.seedOverrideEdits(p);
      },
      error: (err) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not preview the assessment.');
      },
    });
  }

  onCreateRollup(): void {
    if (!this.preview) { this.onPreviewRollup(); return; }
    this.confirmZeroFiling('assessment', () => this.createRollup());
  }

  private createRollup(): void {
    this.saving = true;
    this.casheringService.createRollupAssessment(this.rollupRequest()).subscribe({
      next: () => {
        this.saving = false;
        this.dialogService.openSuccesDialog('Assessment created',
          'The lump-sum assessment return was created over the selected returns.');
        this.backToList();
      },
      error: (err) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not create the assessment.');
      },
    });
  }

  // ===== Assessment (SWAG / estimated periods) =====

  get periods(): FormArray {
    return this.assessmentForm.get('periods') as FormArray;
  }

  private newPeriodGroup(): FormGroup {
    return this.fb.group({
      month: ['', Validators.required],
      year: [new Date().getFullYear(), Validators.required],
      additionalTax: [null, [Validators.required, Validators.min(0)]],
      penalty: [null],
      interest: [null],
    });
  }

  addPeriod(): void { this.periods.push(this.newPeriodGroup()); }
  removePeriod(i: number): void { if (this.periods.length > 1) { this.periods.removeAt(i); } }

  onCreateAssessment(): void {
    if (this.assessmentForm.invalid || !this.account?.id) {
      this.assessmentForm.markAllAsTouched();
      return;
    }
    const raw = this.assessmentForm.value;
    if (raw.overrideSol && !(raw.justification || '').trim()) {
      this.dialogService.openErrorDialog('Justification required',
        'A justification is required to assess outside the statute of limitations.');
      return;
    }
    this.saving = true;
    this.casheringService.createAssessment({
      accountId: this.account.id,
      cause: raw.cause,
      auditReference: raw.auditReference || undefined,
      statuteBasis: raw.statuteBasis || undefined,
      overrideSol: !!raw.overrideSol,
      justification: raw.justification || undefined,
      periods: (raw.periods as any[]).map(p => ({
        period: `${p.year}-${p.month}`,
        additionalTax: Number(p.additionalTax),
        penalty: p.penalty != null ? Number(p.penalty) : undefined,
        interest: p.interest != null ? Number(p.interest) : undefined,
      })),
    }).subscribe({
      next: () => {
        this.saving = false;
        this.dialogService.openSuccesDialog('Assessment created',
          'The assessment case and its returns were created.');
        this.backToList();
      },
      error: (err) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Error', err?.error?.message || 'Could not create the assessment.');
      },
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  // ===== List =====

  loadReturns(): void {
    if (!this.account?.id) { return; }
    this.loading = true;
    this.casheringService.getReturnsByAccount(this.account.id).subscribe({
      next: (returns) => {
        this.allReturns = returns ?? [];
        this.buildYears();
        this.computeCounters();
        this.applyFilters();
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
  }

  private buildYears(): void {
    const set = new Set<number>();
    // Always offer a usable range (so the year selects populate even with no returns yet).
    const base = new Date().getFullYear();
    for (let y = base - 5; y <= base + 1; y++) { set.add(y); }
    this.allReturns.forEach(r => { const y = this.yearOf(r); if (y) set.add(y); });
    this.years = Array.from(set).sort((a, b) => b - a);
  }

  private computeCounters(): void {
    this.unpaidCount = this.allReturns.filter(r => r.status === 'UNPAID' || r.status === 'PENDING').length;
    this.partiallyPaidCount = this.allReturns.filter(r => r.status === 'PARTIALLY_PAID').length;
    this.dueCount = this.allReturns.filter(r => r.status === 'OVERDUE').length;
  }

  applyFilters(): void {
    let rows = [...this.allReturns];
    if (this.selectedYear !== 'all') { rows = rows.filter(r => this.yearOf(r) === this.selectedYear); }
    if (this.selectedType !== 'all') { rows = rows.filter(r => this.baseType(r) === this.selectedType); }
    if (this.selectedStatus !== 'all') { rows = rows.filter(r => r.status === this.selectedStatus); }
    this.dataSource.data = rows;
    if (this.paginator) { this.paginator.firstPage(); }
  }

  private yearOf(r: ReturnSummaryDto): number | null {
    if (!r.period) { return null; }
    const y = parseInt(r.period.substring(0, 4), 10);
    return isNaN(y) ? null : y;
  }

  baseType(r: ReturnSummaryDto): string {
    const t = r.type as string;
    if (t === 'INSTALLMENT' || t === 'ASSESSMENT' || t === 'LIEN') { return t; }
    if (r.origin === 'DUPLICATE') { return 'DUPLICATE'; }
    if (r.origin === 'SUPPLEMENTAL') { return 'SUPPLEMENTAL'; }
    if (t === 'AMENDED') { return 'AMENDED'; }
    return 'REGULAR';
  }

  /**
   * Installment / assessment / lien returns carry no property lines (the properties stay on the
   * returns they roll up), so "0" read as a defect (QA 2026-10-05); show a dash instead.
   */
  propertiesLabel(r: ReturnSummaryDto): string {
    const t = r.type as string;
    if (t === 'INSTALLMENT' || t === 'ASSESSMENT' || t === 'LIEN') { return '—'; }
    return String(r.propertyCount ?? (r.properties?.length || 0));
  }

  /** Display label for the base-type tag. */
  typeLabel(r: ReturnSummaryDto): string {
    switch (this.baseType(r)) {
      case 'DUPLICATE': return 'Duplicate';
      case 'SUPPLEMENTAL': return 'Supplemental';
      case 'AMENDED': return 'Amended';
      case 'INSTALLMENT': return 'Installment';
      case 'ASSESSMENT': return 'Assessment';
      case 'LIEN': return 'Lien';
      default: return 'Regular';
    }
  }

  statusClass(status: string): string {
    return 'status-' + (status || '').toLowerCase();
  }

  onRow(r: ReturnSummaryDto): void {
    if (!this.account?.id) { return; }
    if (this.isPlanType(r)) {
      this.openPlanView(r);
      return;
    }
    this.openReturnDetail(r.id, false, !!r.rolledUp, r.rolledUp ? this.planLabelOf(r) : '');
  }

  // ===== Create flow =====

  onNewReturn(): void {
    this.selectedCreateType = 'REGULAR';
    this.view = 'type';
  }

  selectCreateType(card: CreateTypeCard): void {
    if (!card.enabled) { return; }
    this.selectedCreateType = card.key;
  }

  onTypeNext(): void {
    this.preview = undefined;
    this.overrideEdits = {};
    if (this.selectedCreateType === 'REGULAR') {
      this.view = 'regular';
    } else if (this.selectedCreateType === 'ASSESMENT') {
      this.selectedReturnIds.clear();
      this.assessmentMode = 'rollup';
      this.view = 'assessment';
    } else if (this.selectedCreateType === 'INSTALLMENT') {
      this.selectedReturnIds.clear();
      this.view = 'installment';
    } else if (this.selectedCreateType === 'LIEN') {
      this.selectedReturnIds.clear();
      this.view = 'lien';
    }
  }

  /** Derived "/ In X" tag on underlying returns. Priority: Lien > Assessment > Installment. */
  secondaryTag(r: ReturnSummaryDto): string {
    if (this.isPlanType(r)) { return ''; } // the plan return IS the plan
    if (r.lienCaseId) { return 'In Lien'; }
    if (r.assessmentCaseId) { return 'In Assessment'; }
    if (r.installmentPlanId) { return 'In Installment'; }
    return '';
  }

  onCreateRegular(): void {
    if (this.regularForm.invalid || !this.account?.id) {
      this.regularForm.markAllAsTouched();
      return;
    }
    const raw = this.regularForm.value;
    const period = `${raw.year}-${raw.month}`;
    this.saving = true;
    this.casheringService.createReturn({
      accountId: this.account.id,
      period,
      frequency: raw.frequency,
    }).subscribe({
      next: (created) => {
        this.saving = false;
        this.loadReturns();
        this.openReturnDetail(created.id, true);
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.error?.message || 'Could not create the return.';
        this.dialogService.openErrorDialog('Error', msg);
      },
    });
  }

  backToList(): void {
    this.view = 'list';
    this.preview = undefined;
    this.overrideEdits = {};
    this.planReturn = undefined;
    this.planDetail = undefined;
    this.loadReturns();
  }

  backToType(): void {
    this.view = 'type';
    this.preview = undefined;
  }
}
